'use client';

import React, { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Box, Text, useColorModeValue } from '@chakra-ui/react';

// Import KaTeX CSS for math rendering (same as Dify)
import 'katex/dist/katex.min.css';

/**
 * Preprocess LaTeX content - Dify's approach
 * Converts various LaTeX notations to standard format
 */
const preprocessLaTeX = (content: string): string => {
  if (!content) return '';

  let processed = content;

  // Temporarily protect code blocks
  const codeBlocks: string[] = [];
  processed = processed.replace(/```[\s\S]*?```/g, (match) => {
    codeBlocks.push(match);
    return `__CODE_BLOCK_${codeBlocks.length - 1}__`;
  });

  // Convert LaTeX delimiters to standard format (Dify's approach)
  // \[...\] -> $$...$$
  processed = processed.replace(/\\\[([\s\S]*?)\\\]/g, '$$$$1$$');

  // \(...\) -> $...$
  processed = processed.replace(/\\\((.*?)\\\)/g, '$$$1$$');

  // Restore code blocks
  codeBlocks.forEach((block, index) => {
    processed = processed.replace(`__CODE_BLOCK_${index}__`, block);
  });

  return processed;
};

/**
 * Custom URL transform for security (Dify's approach)
 * Allows safe protocols while blocking dangerous ones
 */
const customUrlTransform = (url: string): string | undefined => {
  if (!url) return undefined;

  // Allow fragment identifiers
  if (url.startsWith('#')) return url;

  // Allow protocol-relative URLs
  if (url.startsWith('//')) return url;

  // Allow relative paths
  if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) return url;

  // Whitelist safe protocols
  const safeProtocols = ['https:', 'http:', 'mailto:', 'tel:'];
  try {
    const parsed = new URL(url);
    if (safeProtocols.includes(parsed.protocol)) return url;
  } catch {
    // If URL parsing fails, it might be a relative URL - allow it
    if (!url.includes(':')) return url;
  }

  return undefined;
};

interface MarkdownProps {
  content: string;
  className?: string;
}

/**
 * Markdown component - Matching Dify's implementation exactly
 *
 * Uses ReactMarkdown with the same plugin configuration as Dify:
 * - remarkGfm: GitHub Flavored Markdown (tables, strikethrough, autolinks)
 * - remarkMath: Math expression support
 * - remarkBreaks: Line break handling (single newlines become <br>)
 * - rehypeKatex: LaTeX math rendering
 * - rehypeRaw: Allow raw HTML passthrough
 *
 * Key difference from previous implementation:
 * - Minimal preprocessing (only LaTeX conversion)
 * - Uses rehypeRaw for HTML passthrough
 * - Matches Dify's exact plugin configuration
 */
export const Markdown = memo(({ content, className = '' }: MarkdownProps) => {
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const codeBgLight = useColorModeValue('gray.100', 'gray.700');
  const codeTheme = useColorModeValue(oneLight, oneDark);
  const tableBorderColor = useColorModeValue('gray.200', 'gray.600');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');

  // Preprocess content - only LaTeX conversion (like Dify)
  const processedContent = useMemo(() => preprocessLaTeX(content), [content]);

  return (
    <Box
      className={`markdown-body ${className}`}
      color={textColor}
      sx={{
        '& p': { mb: 3, lineHeight: 'tall' },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          fontWeight: 'bold',
          mt: 4,
          mb: 2,
          lineHeight: 'short'
        },
        '& h1': { fontSize: '2xl' },
        '& h2': { fontSize: 'xl' },
        '& h3': { fontSize: 'lg' },
        '& h4': { fontSize: 'md' },
        '& ul, & ol': { pl: 6, mb: 3 },
        '& li': { mb: 1 },
        '& blockquote': {
          borderLeft: '4px solid',
          borderColor: 'gray.300',
          pl: 4,
          my: 3,
          fontStyle: 'italic',
          color: 'gray.600',
        },
        '& a': {
          color: 'blue.500',
          textDecoration: 'underline',
          _hover: { color: 'blue.600' },
        },
        '& hr': { my: 4, borderColor: 'gray.200' },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
          my: 4,
        },
        '& th, & td': {
          border: '1px solid',
          borderColor: tableBorderColor,
          px: 3,
          py: 2,
        },
        '& th': {
          bg: tableHeaderBg,
          fontWeight: 'semibold',
        },
        '& pre': {
          borderRadius: 'md',
          overflow: 'auto',
          my: 3,
        },
        '& :not(pre) > code': {
          bg: codeBgLight,
          px: 1.5,
          py: 0.5,
          borderRadius: 'sm',
          fontSize: '0.9em',
          fontFamily: 'mono',
        },
        // KaTeX styling
        '& .katex': {
          fontSize: '1.1em',
        },
        '& .katex-display': {
          overflow: 'auto',
          my: 2,
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          [remarkMath, { singleDollarTextMath: true }],
          remarkBreaks,
        ]}
        rehypePlugins={[
          rehypeKatex,
          rehypeRaw,
        ]}
        urlTransform={customUrlTransform}
        components={{
          code({ className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            const isCodeBlock = match && String(children).includes('\n');

            return isCodeBlock ? (
              <SyntaxHighlighter
                style={codeTheme as any}
                language={match[1]}
                PreTag="div"
                customStyle={{
                  margin: '0.75rem 0',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                }}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          h1: ({ children }) => (
            <Text as="h1" fontSize="2xl" fontWeight="bold" mt={4} mb={2}>
              {children}
            </Text>
          ),
          h2: ({ children }) => (
            <Text as="h2" fontSize="xl" fontWeight="semibold" mt={4} mb={2}>
              {children}
            </Text>
          ),
          h3: ({ children }) => (
            <Text as="h3" fontSize="lg" fontWeight="semibold" mt={3} mb={2}>
              {children}
            </Text>
          ),
          p: ({ children }) => (
            <Text mb={3} lineHeight="tall">
              {children}
            </Text>
          ),
          ul: ({ children }) => (
            <Box as="ul" pl={6} mb={3} listStyleType="disc">
              {children}
            </Box>
          ),
          ol: ({ children }) => (
            <Box as="ol" pl={6} mb={3} listStyleType="decimal">
              {children}
            </Box>
          ),
          li: ({ children }) => (
            <Box as="li" mb={1}>
              {children}
            </Box>
          ),
          table: ({ children }) => (
            <Box
              as="table"
              w="100%"
              mb={4}
              border="1px"
              borderColor={tableBorderColor}
              borderRadius="md"
              overflow="hidden"
              style={{ borderCollapse: 'collapse' }}
            >
              {children}
            </Box>
          ),
          thead: ({ children }) => (
            <Box as="thead" bg={tableHeaderBg}>
              {children}
            </Box>
          ),
          tbody: ({ children }) => (
            <Box as="tbody">{children}</Box>
          ),
          tr: ({ children }) => (
            <Box as="tr" borderBottom="1px" borderColor={tableBorderColor}>
              {children}
            </Box>
          ),
          th: ({ children }) => (
            <Box
              as="th"
              p={3}
              textAlign="left"
              fontWeight="semibold"
              fontSize="sm"
              borderRight="1px"
              borderColor={tableBorderColor}
            >
              {children}
            </Box>
          ),
          td: ({ children }) => (
            <Box
              as="td"
              p={3}
              fontSize="sm"
              borderRight="1px"
              borderColor={tableBorderColor}
            >
              {children}
            </Box>
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </Box>
  );
});

Markdown.displayName = 'Markdown';

export default Markdown;
